import { Card, CardContent } from "@/components/ui/card";
import { UserAvatar } from "@/components/ui/user-avatar";
import {
  Calendar,
  Users,
  Euro,
  Clock,
  MapPin,
  Pencil,
  UserCheck,
  Map,
  Loader2
} from "lucide-react";
import { cn, formatBudget, formatTravelDates, formatCreatedDate, calculateDuration } from "@/components/utils/utils";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TravelRequestBadge } from "./TravelRequestBadge";
import { TravelRequestButton } from "./TravelRequestButton";
import { Button } from "@/components/ui/button";
import { useI18n } from '@/hooks/useI18n';
import { UserRole } from "@/lib/auth/models/auth.model";

interface TravelRequestCardProps {
  travelRequest: DashboardTravelRequest;
  userRole: UserRole | null;
  className?: string;
  onViewRequest: (requestId: string) => void;
  onCreateItinerary?: (requestId: string) => void;
  /** Apre il modale della richiesta in modifica. Assente = azione non disponibile. */
  onEditRequest?: (requestId: string) => void;
  /**
   * Solo agency: la richiesta non e' su nessun membro (vedi `needsAgencyAssignment`).
   * Il tasto di modifica prende il posto del promemoria -- diventa "Assegna" con un
   * pallino rosso -- perche' e' da quel modale, tab Assegnazione, che si gira la
   * richiesta a un membro.
   */
  needsAssignment?: boolean;
  /** Apre l'anteprima dell'itinerario scelto. Il pulsante c'e' solo se la richiesta ne ha uno. */
  onViewChosenItinerary?: (itineraryId: string) => void;
  /** L'anteprima e' in caricamento: spinner sul pulsante. */
  chosenItineraryLoading?: boolean;
}

export const TravelRequestCard = ({ travelRequest, userRole, className, onViewRequest, onCreateItinerary, onEditRequest, needsAssignment = false, onViewChosenItinerary, chosenItineraryLoading = false }: TravelRequestCardProps) => {
  const { t } = useI18n();
  
  return (
    <Card
      className={cn(
        "border border-gray-200 rounded-2xl transition-all hover:border-gray-300",
        className,
      )}
    >
      <CardContent className="p-3 h-full flex flex-col">
        {/* Header con data e badge */}
        <div className="flex justify-between items-center mb-4">
          <span className="text-gray-600">
            {formatCreatedDate(travelRequest.createdAt)}
          </span>
          <div className="flex justify-end flex-wrap gap-1">
            <TravelRequestBadge travelRequest={travelRequest} />
          </div>
        </div>

        {/* Titolo del viaggio */}
        <h4 className="text-black mb-4 line-clamp-1">
          {travelRequest.destinations.length > 0
            ? travelRequest.destinations.join(", ")
            : "Destinazione da definire"}
        </h4>

        {/* Dettagli del viaggio */}
        <div className="text-sm flex-1 py-3">
          <div className="grid grid-cols-2 gap-4">
            {/* Date */}
            <div className="flex items-center gap-2 col-span-2">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="text-gray-600">
                {formatTravelDates(
                  travelRequest.travelStartDate,
                  travelRequest.travelEndDate,
                )}
              </span>
            </div>
            {/* Prima colonna */}
            <div className="space-y-3">
              {/* People */}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">
                  {travelRequest.totalGuests || "N/A"}
                </span>
              </div>

              {/* Budget */}
              <div className="flex items-center gap-2">
                <Euro className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">
                  {formatBudget(
                    travelRequest.budgetMin,
                    travelRequest.budgetMax,
                    { showCurrency: false },
                  )}
                </span>
              </div>
            </div>

            {/* Seconda colonna */}
            <div className="space-y-3">
              {/* Duration */}
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">
                  {calculateDuration(
                    travelRequest.travelStartDate,
                    travelRequest.travelEndDate,
                    { t },
                  )}
                </span>
              </div>

              {/* Departure */}
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600 line-clamp-1">
                  {travelRequest.departurePoint ||
                    t("travelRequest.departureTBD")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer con contatto e bottone */}
        <div className="mt-4 space-y-3">
          {/* Contact info */}
          <div className="flex items-center gap-2">
            <UserAvatar
              firstName={travelRequest.contact.firstName}
              lastName={travelRequest.contact.lastName}
              email={travelRequest.contactEmail}
              imageUrl={travelRequest.contact.avatarUrl}
              size="xs"
            />
            <span className="text-sm text-gray-900 font-medium">
              {travelRequest.contactFullName}
            </span>
          </div>

          {/* Richiesta completata: l'itinerario che il cliente ha scelto, in anteprima. */}
          {onViewChosenItinerary && travelRequest.chosenItinerary && (
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => onViewChosenItinerary(travelRequest.chosenItinerary!.id)}
              disabled={chosenItineraryLoading}
              title={travelRequest.chosenItinerary.title ?? undefined}
            >
              {chosenItineraryLoading ? <Loader2 className="animate-spin" /> : <Map />}
              <span className="truncate">
                {travelRequest.chosenItinerary.title || t("requests.viewChosenItinerary")}
              </span>
            </Button>
          )}

          {/* Azioni: l'azione principale prende la riga, la modifica e' un tondo con
              la sola icona accanto. Per l'agency quel tondo e' anche il promemoria di
              assegnazione: icona rossa e pallino, con l'etichetta nel tooltip. */}
          {onEditRequest ? (
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <TravelRequestButton
                  travelRequest={travelRequest}
                  onClick={onViewRequest}
                  onCreateItinerary={onCreateItinerary}
                  variant="card"
                  userRole={userRole}
                />
              </div>
              <div className="relative shrink-0">
                <Button
                  variant="outline"
                  size="icon"
                  className={cn(
                    "h-12 w-12",
                    needsAssignment && "border-red-300 text-red-600 hover:bg-red-50",
                  )}
                  onClick={() => onEditRequest(travelRequest.id)}
                  title={String(needsAssignment ? t('travelRequest.assign') : t('travelRequest.editRequest'))}
                  aria-label={String(needsAssignment ? t('travelRequest.assign') : t('travelRequest.editRequest'))}
                >
                  {needsAssignment ? <UserCheck /> : <Pencil />}
                </Button>
                {needsAssignment && (
                  <span
                    className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                    aria-hidden
                  />
                )}
              </div>
            </div>
          ) : (
            <TravelRequestButton
              travelRequest={travelRequest}
              onClick={onViewRequest}
              onCreateItinerary={onCreateItinerary}
              variant="card"
              userRole={userRole}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};
